import { isEqual } from "lodash";
import type { MutableRefObject } from "react";

import type { FieldData as DocumentFieldData } from "../DocumentExport/DocumentExportContent";
import type { FieldData as ImageFieldData } from "../ImageExport/ImageExportContent";
import { isNullish } from "@theorgchartdev/storybook";
import { NormalizeSCWiz } from "@/appComponents/Navigation/RightSidebar/Optimize/Optimize.utils";
import { ReRenderCanvas } from "@/Utils/StateLib";
import {
  enumBookScope,
  enumIWizDataSet,
  enumPageSizeUnits,
  enumSubChartOption,
  enumDataSource,
  enumPanelRenderMode,
  enumFillMode,
  enumPageSizes,
  enumPageOrientationOptions,
  enumFilterModes
} from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { isInPlanning } from "@coreapp/utils/isInPlanning";
import { QPublishScalerPage } from "@coreapp/CoreApp/QPublishScalerPage";
import { QSubChart } from "@coreapp/CoreApp/QSubChart";
import { DoOptimizePages } from "@coreapp/utils/DoOptimizePages";
import { QIWizData } from "@coreapp/CoreApp/QIWizData";
import { QChartBox } from "@coreapp/CoreApp/QChartBox";
import { yieldToPaint } from "@coreapp/utils/yieldToPaint";

type DocumentRef = MutableRefObject<QXDoc | null>;

type Params = {
  mockDocumentRef: DocumentRef;
  sourceDocumentRef: DocumentRef;
  backgroundRef: MutableRefObject<{ color: string; fillMode: number }>;
  nextFieldData: DocumentFieldData | ImageFieldData;
  updatedKey: keyof DocumentFieldData | keyof ImageFieldData;
};

export const handlePreviewUpdate = (params: Params) => async (abortController: AbortController) => {
  const { backgroundRef, nextFieldData } = params;
  const mockDocument = params.mockDocumentRef.current;
  const sourceDocument = params.sourceDocumentRef.current;

  if (isNullish(mockDocument) || isNullish(sourceDocument)) {
    abortController.abort();
    return;
  }

  // Ungroup current preview page before scope/pagination restructuring.
  const previewWsMgr = mockDocument.GetDocMgr()?.GetWSMgr();
  if (previewWsMgr && mockDocument.IsGroupingApplied()) {
    const staleBoxList = previewWsMgr.GetTargetTrees(false, mockDocument, false) || [];
    if (staleBoxList.length) mockDocument.UngroupBoxesAndReapplyLayout(staleBoxList);
  }

  await applyPagination(mockDocument, sourceDocument, nextFieldData);
  if (abortController.signal.aborted) return;

  await applyTopbox(mockDocument, sourceDocument, nextFieldData);
  if (abortController.signal.aborted) return;

  mockDocument.ApplyChartGroupingToDoc();
  applyBranchRoot(mockDocument, nextFieldData);

  if ("pageSize" in nextFieldData) {
    applyPageSize(
      mockDocument,
      sourceDocument,
      nextFieldData["pageSize"],
      nextFieldData["pageOrientation"]
    );
  }
  zoomToFit(mockDocument);
  applyPlanningOverlay(mockDocument, nextFieldData);
  applyPlanningActivity(mockDocument, nextFieldData);
  applyArrowVisibility(mockDocument, sourceDocument, nextFieldData);
  applyIncludeBackground(mockDocument, nextFieldData);
  applyWhiteBackground(mockDocument, backgroundRef, nextFieldData, true);
  ReRenderCanvas();
};

export const handleSetup = async (
  mockDoc: QXDoc,
  sourceDoc: QXDoc,
  backgroundRef: Params["backgroundRef"],
  fieldData: DocumentFieldData | ImageFieldData
) => {
  removeEditArtifacts(mockDoc);

  await yieldToPaint();

  await applyPagination(mockDoc, sourceDoc, fieldData);
  await applyTopbox(mockDoc, sourceDoc, fieldData);
  applyBranchRoot(mockDoc, fieldData);

  if ("pageSize" in fieldData) {
    await yieldToPaint();
    applyPageSize(mockDoc, sourceDoc, fieldData["pageSize"], fieldData["pageOrientation"]);
  }
  if (!mockDoc.IsPlanChart()) {
    mockDoc.ApplyChartGroupingToDoc();
  }
  zoomToFit(mockDoc);
  applyPlanningOverlay(mockDoc, fieldData);
  applyPlanningActivity(mockDoc, fieldData);
  applyArrowVisibility(mockDoc, sourceDoc, fieldData);
  applyIncludeBackground(mockDoc, fieldData);
  applyWhiteBackground(mockDoc, backgroundRef, fieldData, true);
  ReRenderCanvas();
};

const removeEditArtifacts = (doc: QXDoc) => {
  const canvasPanel = doc.GetCanvasRef();
  if (!canvasPanel) {
    return;
  }

  canvasPanel.fAltBackground?.SetShownState(false);
  canvasPanel.fBoxDragEnabled = false;
  canvasPanel.SetShowPageMargins(false);
  canvasPanel.SetShowHeaderFooter(false);
};

export const resetPreview = async (
  mockDoc: QXDoc,
  sourceDoc: QXDoc,
  fieldData: DocumentFieldData | ImageFieldData
) => {
  const canvasPanel = mockDoc.GetCanvasRef();
  if (!canvasPanel) {
    return;
  }

  canvasPanel.fAltBackground?.SetShownState(true);
  clearPlanningActivity(mockDoc);
  await applyTopbox(mockDoc, sourceDoc, fieldData);
};

const applyPlanningOverlay = (mockDoc: QXDoc, fieldData: DocumentFieldData | ImageFieldData) => {
  if (!("showActivity" in fieldData)) {
    return;
  }

  if (!isInPlanning()) return;
  const docMgr = mockDoc.GetDocMgr();
  const wsMgr = docMgr?.GetWSMgr();
  if (!wsMgr) return;

  if (Globals._pubSettings) {
    Globals._pubSettings.fShowOverlay = fieldData.showOverlay;
  }
  wsMgr.ReloadCurrentSubChart();
};

export const clearPlanningActivity = (mockDoc: QXDoc) => {
  if (!isInPlanning()) {
    return;
  }

  const docMgr = mockDoc.GetDocMgr();
  const wsMgr = docMgr?.GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();
  const mockCanvasPanel = mockDoc.GetCanvasRef();
  if (!planMgr || !mockCanvasPanel) {
    return;
  }

  const pageMgr = mockDoc.GetPageMgr();

  mockCanvasPanel.RemoveActivityContent();
  planMgr.RestorePageContentPubMargin(pageMgr);
};

export const applyPlanningActivity = (
  mockDoc: QXDoc,
  fieldData: DocumentFieldData | ImageFieldData
) => {
  if (!("showActivity" in fieldData)) {
    return;
  }

  if (!isInPlanning()) return;
  clearPlanningActivity(mockDoc);

  const docMgr = mockDoc.GetDocMgr();
  const wsMgr = docMgr?.GetWSMgr();
  const mockCanvasPanel = mockDoc.GetCanvasRef();
  const planMgr = wsMgr?.GetPlanMgr();
  const pageMgr = mockDoc.GetPageMgr();
  if (!wsMgr || !mockCanvasPanel || !pageMgr || !planMgr) {
    return;
  }

  if (fieldData.showActivity !== "Off") {
    let margRect = pageMgr.GetPageMarginsInt(true);
    margRect = planMgr.AddDocPubMargin(pageMgr.GetPageProps(), margRect);

    // Force re-layout since activity overlay margins changed the page margins.
    // Without this, ReloadCurrentSubChart skips RecalculatePageLayout (NeedOptimize=false)
    // and chart boxes keep stale positions from the prior layout, overlapping the overlay.
    const scm = mockDoc.GetSubChartMgr();
    const curSC = scm?.GetCurrentSubChart();
    if (curSC) curSC.SetNeedOptimize(true);

    wsMgr.ReloadCurrentSubChart();

    const pWidth = pageMgr.GetMinPageWidth();
    const pHeight = pageMgr.GetMinPageHeight();

    const basePage = new QPublishScalerPage();
    basePage.InitPage(pWidth, pHeight, margRect, 1);
    mockCanvasPanel.AddActivityContent(planMgr, fieldData.showActivity, basePage);
  } else {
    wsMgr.ReloadCurrentSubChart();
  }
};

export const zoomToFit = (mockDoc: QXDoc) => {
  const mockCanvasPanel = mockDoc.GetCanvasRef();
  if (!mockCanvasPanel) {
    return;
  }

  const priorScaleFactor = mockCanvasPanel.GetScaleFactor();

  const canvasStyle = mockCanvasPanel.fBackBuffer.style;
  const scaleFactor = Math.min(
    parseInt(canvasStyle.width) / mockCanvasPanel.fCanvasSizeX,
    parseInt(canvasStyle.height) / mockCanvasPanel.fCanvasSizeY
  );

  mockCanvasPanel.SetScaleFactor(scaleFactor);
  mockCanvasPanel.ResizeCanvasCenter(priorScaleFactor);
  mockCanvasPanel.DoCustomResize();
};

const navToBox = async (mockDoc: QXDoc, targetID: string, checkIfTop = false) => {
  const docMgr = mockDoc.GetDocMgr();
  const wsMgr = docMgr?.GetWSMgr();
  if (!wsMgr) {
    return;
  }

  // In publish mode, subchartSwitch broadcast is suppressed (to prevent search menu re-render).
  // DoNavToBox → SwitchToSubChart is synchronous, so navigation completes immediately.
  if (Globals._publishRef !== null) {
    wsMgr.DoNavToBox({ targetID }, true, false, true);
    return;
  }

  return new Promise<void>(resolve => {
    const unsubscribe = wsMgr.SubscribeToTopic(
      "subchartSwitch",
      ([_, newSc]: [QSubChart, QSubChart]) => {
        if (!newSc) {
          return;
        }

        const topBox = newSc.GetTopDBObj();
        const topBoxId = topBox?.GetObjectRecordID();
        if (checkIfTop && topBoxId !== targetID) {
          return;
        }

        unsubscribe();
        resolve();
      }
    );

    wsMgr.DoNavToBox({ targetID }, true, false, true);
  });
};

const applyTopbox = async (
  mockDoc: QXDoc,
  sourceDoc: QXDoc,
  fieldData: DocumentFieldData | ImageFieldData
) => {
  const mockCanvasPanel = mockDoc.GetCanvasRef();
  const mockScm = mockDoc.GetSubChartMgr();
  const sourceScm = sourceDoc.GetSubChartMgr();
  const hasCoC = mockDoc.GetRequestedChainLevels() > 0;
  if (hasCoC || !mockCanvasPanel) {
    return;
  }

  let targetPersonId = sourceScm?.GetCurrentTopObjPersonID();
  if ("scope" in fieldData && fieldData.scope === "Book") {
    const topScId = sourceScm?.GetRootSubChartID();
    let topSc = sourceScm?.FindSubChart(topScId);
    if (!topSc) {
      const curSc = sourceScm?.GetCurrentSubChart();
      topSc = curSc.GetRootSubChart();
    }
    // Use the record ID string (matches GetCurrentTopObjPersonID above and what
    // DoNavToBox expects). GetTopDBObjRef returns fTopObjRef, an internal object
    // reference used with LookupObjByRef — passing it to DoNavToBox makes nav
    // no-op, so "Entire chart" scope silently stays on the current branch.
    targetPersonId = topSc.GetTopDBObj()?.GetObjectRecordID();
  }

  await navToBox(mockDoc, targetPersonId, false);

  if (!("scope" in fieldData) || ["Page", "Branch"].includes(fieldData.scope)) {
    const mockTopPersonId = mockScm?.GetCurrentTopObjPersonID();

    if (mockTopPersonId !== targetPersonId) {
      mockCanvasPanel.InsertSubChartBreak();
      await navToBox(mockDoc, targetPersonId, true);
    }
  }

  mockCanvasPanel.ClearSelection();
};

const BookScopeMap: Record<
  DocumentFieldData["scope"],
  (typeof enumBookScope)[keyof typeof enumBookScope]
> = {
  Page: enumBookScope.page,
  Branch: enumBookScope.branch,
  Book: enumBookScope.entire
};

export const countExportableRecords = (doc: QXDoc, scope: DocumentFieldData["scope"]): number => {
  const topBox = doc.GetObjDB()?.fSubChartContext;

  if (!topBox) return 0;

  switch (scope) {
    case "Page":
      return topBox.CountSubsInSubChart(false, false) + 1;
    case "Book":
    case "Branch":
      return Number(topBox.SmartGetFieldValue("Headcount")) || 0;
    default:
      return 0;
  }
};

const applyBranchRoot = (mockDoc: QXDoc, fieldData: DocumentFieldData | ImageFieldData) => {
  const targetScope = "scope" in fieldData ? fieldData.scope : "Page";

  const scm = mockDoc.GetSubChartMgr();
  let branchSc = scm?.GetCurrentSubChart();
  if (targetScope === "Book") {
    branchSc = null;
  }
  scm?.SetBranchRoot(branchSc, BookScopeMap[targetScope]);
};

const applyPagination = async (
  mockDoc: QXDoc,
  sourceDoc: QXDoc,
  fieldData: DocumentFieldData | ImageFieldData
) => {
  const docMgr = mockDoc.GetDocMgr();
  const wsMgr = docMgr?.GetWSMgr();
  if (!wsMgr) return;

  const mockScm = mockDoc.GetSubChartMgr();
  const scWiz = mockDoc.GetIWizData(enumIWizDataSet.interactive).Clone();
  const sourceIWiz = sourceDoc.GetIWizData(enumIWizDataSet.interactive);

  applyMockDocFilters(mockDoc, sourceDoc, fieldData);

  // Clear branch root to prevent subcharts from being lost when syncing subchart list from source document
  // Because we are clearing the scope here, the maximum record limit has a possibility of being hit
  // To prevent this, we temporarily set the maximum record limit to infinity
  // const initSinglePageLimit = gAccountInfo.GetMaxRecords(true);
  // gAccountInfo.SetMaxRecords([Number.POSITIVE_INFINITY, gAccountInfo.GetMaxRecords(false)]);
  // mockScm.SetBranchRoot(null, BookScopeMap["Book"]);
  await applyTopbox(mockDoc, sourceDoc, fieldData);
  // gAccountInfo.SetMaxRecords([initSinglePageLimit, gAccountInfo.GetMaxRecords(false)]);

  const topbox = mockScm?.GetCurrentTopObj();
  const offsetLevel = topbox.DoLevelCalc() - 1;

  const shouldPreservePagination =
    !("scChartBreakType" in fieldData) ||
    fieldData.scChartBreakType === enumSubChartOption.keepAsIs ||
    fieldData.scope === "Page";

  if (shouldPreservePagination) {
    if (sourceDoc.IsServerChart()) {
      /* Emulate server-side pagination w/ break by level */
      scWiz.SetSubChartOption(enumSubChartOption.breakbylevel);

      const mockSc = mockScm?.GetCurrentSubChart();
      const topBox = mockSc.GetTopDBObj();
      let startLevel = topBox.DoLevelCalc();
      if (mockDoc.GetRequestedChainLevels() > 0) {
        startLevel = mockDoc.GetRequestedLevels() + mockDoc.GetRequestedChainLevels() + 1;
      }

      scWiz.SetSmartStartLevel(`Level ${startLevel}`);
      scWiz.SetBreakLevels(`${sourceDoc.GetRequestedLevels()} Levels`);
      scWiz.SetSmartBreak("1 Boxes");
    } else {
      /* Maintain source doucument's pagination settings */
      scWiz.SetSubChartOption(sourceIWiz.GetSubChartOption());
      scWiz.SetSmartStartLevel(sourceIWiz.GetSmartStartLevel());
      scWiz.SetSubChartNameFieldID(sourceIWiz.GetSubChartNameFieldID());
      scWiz.SetPageDensity(sourceIWiz.GetPageDensity());
      scWiz.SetBreakLevels(sourceIWiz.GetBreakLevels());
      scWiz.SetBreakField(sourceIWiz.GetBreakField());
      scWiz.SetSmartBreak(sourceIWiz.GetSmartBreak());
    }
  } else {
    /* Apply pagination settings from user */

    const startLevel = offsetLevel + parseInt(fieldData.startbreaksat);
    scWiz.SetSubChartOption(fieldData.scChartBreakType);
    scWiz.SetSmartStartLevel(`Level ${startLevel}`);
    scWiz.SetSubChartNameFieldID(fieldData.nameusing);
    scWiz.SetPageDensity(fieldData.pagedensity);
    scWiz.SetBreakLevels(`${fieldData.breakevery} Levels`);
    scWiz.SetBreakField(fieldData.breakfield);
    scWiz.SetSmartBreak(`${fieldData.minsubsforbreak} Boxes`);
  }

  const paginationActive = isPaginationActive(mockDoc, scWiz);
  const filterActive = isFilterActive(mockDoc);
  if (!paginationActive || filterActive) {
    // The topbox navigation above re-applies grouping (SwitchToSubChart re-groups and
    // skips its own ungroup during preview/publish). Break targets that are grouped
    // managers can't be located by FindSCNode, which silently drops their page breaks.
    // Ungroup here so DoAutoBreaks (run first inside HandleApplySubChartWiz) computes
    // breaks on the ungrouped tree; the end-of-cycle ApplyChartGroupingToDoc restores
    // grouping for display.
    if (mockDoc.IsGroupingApplied()) {
      const groupedBoxList = wsMgr.GetTargetTrees(false, mockDoc, false) || [];
      if (groupedBoxList.length) mockDoc.UngroupBoxesAndReapplyLayout(groupedBoxList);
    }

    // Apply pagination settings
    wsMgr.ClearUndoRedo();
    wsMgr.HandleApplySubChartWiz();

    // If pagination fails, reset to "safe" settings
    // if (!appliedSuccessfully) {
    //   wsMgr.SetSafeSubChartWiz(offsetLevel);

    //   if ("scChartBreakType" in fieldData) {
    //     fieldData.scChartBreakType = scWiz.GetSubChartOption();
    //     fieldData.startbreaksat = `${scWiz.GetSmartStartVal() - offsetLevel}`;
    //     fieldData.breakevery = `${scWiz.GetBreakLevelsVal()}`;
    //     fieldData.minsubsforbreak = `${scWiz.GetSmartBreakVal()}`;
    //   }

    //   wsMgr.HandleApplySubChartWiz();
    // }

    // Restore previous subchart breaks
    if (shouldPreservePagination) {
      const sourceDocMgr = sourceDoc.GetDocMgr();
      const mockIWiz = mockDoc.GetIWizData(enumIWizDataSet.interactive);
      mockIWiz.SetPriorDocMgr(sourceDocMgr);
      mockScm?.CopySubChartBreaks(mockIWiz);
    }

    // Restore previously named subcharts and order
    if (sourceIWiz.GetDataSource() === enumDataSource.chartbuilder) {
      const sourceScm = sourceDoc.GetSubChartMgr();
      mockScm?.CopySubChartOrder(sourceScm);
      mockScm?.CopySubChartNames(sourceDoc);
    }
  }

  // Apply subchart sort
  if ("scSortKey" in fieldData && !fieldData.shouldPreserveSubchartSort && fieldData.scSortKey) {
    mockScm?.SetSubChartSort({
      enabled: true,
      sortKey: fieldData.scSortKey
    });
    mockScm?.SortSubCharts();
  }
};

const isPaginationActive = (doc: QXDoc, subChartWiz: QIWizData) => {
  const iWiz = doc.GetIWizData(enumIWizDataSet.interactive);
  const existingSubChartConfig = NormalizeSCWiz(iWiz);
  const targetSubChartConfig = NormalizeSCWiz(subChartWiz);

  const areEqual = isEqual(existingSubChartConfig, targetSubChartConfig);

  if (!areEqual) {
    iWiz.SetSubChartOption(subChartWiz.GetSubChartOption());
    iWiz.SetSmartStartLevel(subChartWiz.GetSmartStartLevel());
    iWiz.SetSubChartNameFieldID(subChartWiz.GetSubChartNameFieldID());
    iWiz.SetPageDensity(subChartWiz.GetPageDensity());
    iWiz.SetBreakLevels(subChartWiz.GetBreakLevels());
    iWiz.SetBreakField(subChartWiz.GetBreakField());
    iWiz.SetSmartBreak(subChartWiz.GetSmartBreak());
  }

  return areEqual;
};

const isFilterActive = (doc: QXDoc) => {
  const mockfilterMgr = doc.GetFilterMgr();
  return mockfilterMgr?.HasSavedFiltersApplied() || mockfilterMgr?.HasActiveFilter();
};

const applyMockDocFilters = (
  mockDoc: QXDoc,
  sourceDoc: QXDoc,
  options: DocumentFieldData | ImageFieldData
) => {
  const mockfilterMgr = mockDoc.GetFilterMgr();
  const hasFiltersApplied = isFilterActive(mockDoc);
  if (mockfilterMgr && hasFiltersApplied) {
    const sourceFilterMode = sourceDoc.GetFilterMgr()?.GetFilterMode() || enumFilterModes.mute;
    const includeFilteredMgrs = "includefilteredmgrs" in options && options.includefilteredmgrs;
    const activeFilters = mockDoc.ResetFilters();
    mockfilterMgr.fFilterMode = includeFilteredMgrs
      ? enumFilterModes.includefilteredmgrs
      : sourceFilterMode;
    mockDoc.RestoreFilters(activeFilters);
  }
};

export const applyArrowVisibility = (
  mockDoc: QXDoc,
  sourceDoc: QXDoc,
  fieldData: DocumentFieldData | ImageFieldData
) => {
  const sourceScm = sourceDoc.GetSubChartMgr();
  const sourceSc = sourceScm?.GetCurrentSubChart();
  const sourceTopBoxId = sourceSc.GetTopDBObj()?.GetObjectRecordID();
  const mockCanvasPanel = mockDoc.GetCanvasRef();
  if (!mockCanvasPanel) {
    return;
  }

  const elements = mockCanvasPanel.fElements ?? [];

  for (const element of elements) {
    if (!(element instanceof QChartBox)) {
      continue;
    }

    const dataObj = element.GetDataObj();
    const recordId = dataObj?.GetObjectRecordID();
    element.RemoveSubChartDrill();

    if (
      !("navigationArrows" in fieldData) ||
      fieldData.navigationArrows === false ||
      fieldData.scope === "Page" ||
      (fieldData.scope === "Branch" && recordId === sourceTopBoxId)
    ) {
      continue;
    }

    element.AddSubChartDrill();
  }
};

const applyIncludeBackground = (mockDoc: QXDoc, fieldData: DocumentFieldData | ImageFieldData) => {
  if (!("includeBackground" in fieldData)) {
    return;
  }

  const canvasPanel = mockDoc.GetCanvasRef();
  if (!canvasPanel) {
    return;
  }

  const nextIncludeBackground = fieldData["includeBackground"];
  const nextRenderMode = nextIncludeBackground
    ? enumPanelRenderMode.all
    : enumPanelRenderMode.contentOnly;

  canvasPanel.SetRenderMode(nextRenderMode);
};

const applyWhiteBackground = (
  mockDoc: QXDoc,
  backgroundRef: Params["backgroundRef"],
  fieldData: DocumentFieldData | ImageFieldData,
  isSetup = false
) => {
  const nextWhiteBackground = fieldData["whiteBackground"];
  const pageMgr = mockDoc.GetPageMgr();
  if (!pageMgr) {
    return;
  }

  if (isSetup) {
    backgroundRef.current = {
      color: pageMgr.GetBackgroundColor(),
      fillMode: pageMgr.GetBackgroundFillMode()
    };
  }

  if (nextWhiteBackground) {
    pageMgr.SetBackgroundColor("#FFFFFF");
    pageMgr.SetBackgroundFillMode(enumFillMode.solidFill);
  } else {
    pageMgr.SetBackgroundColor(backgroundRef.current.color);
    pageMgr.SetBackgroundFillMode(backgroundRef.current.fillMode);
  }
};

const isPageSize = (size: string): size is keyof typeof enumPageSizes => {
  return enumPageSizes.hasOwnProperty(size);
};

const getDimensions = (size: string) => {
  if (isPageSize(size)) {
    return enumPageSizes[size];
  }

  return enumPageSizes.Letter;
};

const applyPageSize = (doc: QXDoc, sourceDoc: QXDoc, size: string, orientation: number) => {
  const canvasPanel = doc.GetCanvasRef();
  const docMgr = doc.GetDocMgr();
  const wsMgr = docMgr?.GetWSMgr();
  const pageMgr = docMgr?.GetPageMgr();
  let pageProps = pageMgr?.GetPageProps();
  if (!wsMgr || !canvasPanel || !pageMgr || !pageProps) {
    return;
  }

  pageMgr.SetPageOrientation(orientation);

  if (size === "current") {
    const sourcePageMgr = sourceDoc.GetPageMgr();
    pageProps = sourcePageMgr?.GetPageProps();

    if (!sourcePageMgr || !pageProps) {
      return;
    }
  } else {
    const sizeArray = getDimensions(size);
    if (orientation === enumPageOrientationOptions.landscape) {
      pageProps.fPageHeight = sizeArray[0];
      pageProps.fPageWidth = sizeArray[1];
    } else {
      pageProps.fPageHeight = sizeArray[1];
      pageProps.fPageWidth = sizeArray[0];
    }

    const docPrefs = doc.GetDocPrefs();
    if (!docPrefs) {
      return;
    }

    const pageOpt = docPrefs.GetPageOpt();

    if (size.indexOf("ppt") !== -1) {
      pageOpt.SetAspectRatio(size === "ppt1ps" ? 5 : 7);
    } else {
      pageOpt.SetRealAspectRatio(pageProps.fPageWidth / pageProps.fPageHeight);
    }
  }

  const plm = doc.GetPageLayoutMgr();
  plm?.ForceAspectRatio(pageProps.fPageWidth / pageProps.fPageHeight);
  canvasPanel.ChangePageProps(pageProps, enumPageSizeUnits.pixels);
  DoOptimizePages(wsMgr);
  canvasPanel.PanToTopCenter();
};
